import { v4 as uuid } from "uuid";
import { z } from "zod";
import { createDefaultPackagePolicy } from "../../entities/packagePolicy";
import { projectToPackageEnvironmentalCommitment } from "../../interfaces/packageEnvironmentalCommitment";
import { projectToPackageLabourHourCommitment } from "../../interfaces/packageLabourHourCommitment";
import { projectToPackageSocialSpendCommitment } from "../../interfaces/packageSocialSpendCommitment";
export const packageProgressSchema = z.object({
    accepted: z.number(), // Percentage of ReportStatus.Accepted reports
    complete: z.number(), // Percentage of ReportStatus.Complete reports
    overdue: z.number() // Calculated as 100 - accepted - complete (all other statuses)
});
/**
 * How far down the package tree a request reads — the wire contract behind the
 * analytics Work Package filter. `Descendants` (the roll-up, and the default) or
 * the viewing package's own hours alone, so that "All Work Packages" equals the
 * sum of the individual selections. A subset of the backend's internal
 * `ContractorPackageSubtreeMode`.
 */
export var PackageScopeMode;
(function (PackageScopeMode) {
    PackageScopeMode["Descendants"] = "descendants";
    PackageScopeMode["Self"] = "self";
})(PackageScopeMode || (PackageScopeMode = {}));
export var PackageType;
(function (PackageType) {
    PackageType["OneTime"] = "package-type_one-time";
    PackageType["HeadContractorPackage"] = "package-type_head-contractor-package";
    PackageType["BalustradesAndHandrail"] = "package-type_balustrades-and-handrail";
    PackageType["Carpentry"] = "package-type_carpentry";
    PackageType["Civil"] = "package-type_civil";
    PackageType["ConcretingAndFormwork"] = "package-type_concreting-and-Formwork";
    PackageType["Consultants"] = "package-type_consultants";
    PackageType["CurtainsAndBlinds"] = "package-type_curtains-and-Blinds";
    PackageType["Demolition"] = "package-type_demolition";
    PackageType["DryFire"] = "package-type_dry_fire";
    PackageType["Design"] = "package-type_design";
    PackageType["Electrical"] = "package-type_electrical";
    PackageType["Excavating"] = "package-type_excavating";
    PackageType["Facade"] = "package-type_facade";
    PackageType["FacadeAndCladding"] = "package-type_facade-and-cladding";
    PackageType["Fencing"] = "package-type_fencing";
    PackageType["Flooring"] = "package-type_flooring";
    PackageType["Hydraulics"] = "package-type_hydraulics";
    PackageType["KitchensAndBars"] = "package-type_kitchens-and-bars";
    PackageType["LabourHire"] = "package-type_labour_hire";
    PackageType["Landscaping"] = "package-type_landscaping";
    PackageType["Masonry"] = "package-type_masonry";
    PackageType["Mechanical"] = "package-type_mechanical";
    PackageType["MechanicalGas"] = "package-type_mechanical-gas";
    PackageType["Miscellaneous"] = "package-type_miscellaneous";
    PackageType["Other"] = "package-type_other";
    PackageType["Painting"] = "package-type_painting";
    PackageType["Plastering"] = "package-type_plastering";
    PackageType["Plumbing"] = "package-type_plumbing";
    PackageType["RoofSafetySystems"] = "package-type_roof-safety-systems";
    PackageType["Roofing"] = "package-type_roofing";
    PackageType["Scaffolding"] = "package-type_scaffolding";
    PackageType["Security"] = "package-type_security";
    PackageType["Signage"] = "package-type_signage";
    PackageType["Structure"] = "package-type_structure";
    PackageType["StructuralAndFraming"] = "package-type_structural-and-framing";
    PackageType["StructuralSteel"] = "package-type_structural-steel";
    PackageType["Tiling"] = "package-type_tiling";
    PackageType["WetFire"] = "package-type_wet-fire";
})(PackageType || (PackageType = {}));
export const contractorPackageSchema = z.object({
    abn: z.string(),
    /** Canonical (suffix-stripped) form of organisation_name, derived by the
     * database. Optional: absent on payloads predating the derived column. */
    canonical_name: z.string().nullish(),
    closed_date: z.string().optional(),
    commitments_environmental: z.array(z.custom()),
    commitments_hours: z.array(z.custom()),
    commitments_spend: z.array(z.custom()),
    contractor_id: z.string().optional(),
    contractor_package_id: z.string(),
    created: z.date(),
    depth: z.number(),
    enabled_local_content: z.array(z.string()).optional(),
    estimated_progress: packageProgressSchema,
    financial: z.custom(),
    financial_id: z.string(),
    first_report_month: z.string().optional(),
    is_accepted: z.boolean(),
    is_closed: z.boolean(),
    is_draft: z.boolean(),
    local_content: z.array(z.custom()),
    local_content_targets: z.array(z.custom()),
    organisation_logo: z.string(),
    organisation_name: z.string(),
    package_contact: z.string().optional(),
    package_description: z.string().optional(),
    /** Estimated; bounds the reporting schedule. Optional, unlike the start. */
    package_end_date: z.string().optional(),
    package_number: z.string(),
    package_start_date: z.string(),
    package_type: z.enum(PackageType),
    parent_package_id: z.string().optional(),
    policies: z.array(z.custom()),
    project_id: z.string(),
    project_name: z.string().optional(),
    report_exists: z.boolean().optional(),
    representative_email: z.string(),
    send_invite: z.boolean().optional(),
    tree_path: z.string(),
    use_labour_hour: z.boolean(),
    use_local_content: z.boolean(),
    use_policy: z.boolean(),
    use_social_spend: z.boolean(),
    use_sustainability: z.boolean()
});
export const contractorPackageSetupSchema = z.object({
    contractor_package: contractorPackageSchema,
    organisation: z.custom().optional(),
    profile: z.custom().optional()
});
export const contractorPackageDashboardSchema = z.object({
    contractor_package: contractorPackageSchema,
    summary: z.object({
        documents_status: z.object({
            documents_progress: z.number(),
            documents_submitted: z.number(),
            documents_total: z.number()
        }),
        labour_hours: z.object({
            actual_hours: z.number(),
            hours_progress: z.number(),
            target_hours: z.number()
        }),
        local_content: z.object({
            anz_value_add: z.number(),
            local_content_target: z.number(),
            value_reported_to_date: z.number()
        }),
        reports_status: z.object({
            reports_accepted: z.number(),
            reports_total: z.number()
        }),
        social_spend: z.object({
            actual_spend: z.number(),
            spend_progress: z.number(),
            target_spend: z.number()
        })
    })
});
export const createDefaultHeadContractorPackage = (project, packageType, packageNumber, financial) => {
    const packageFinancial = {
        ...financial,
        financial_id: uuid()
    };
    return {
        abn: "",
        closed_date: undefined,
        commitments_environmental: (project.commitments_environmental || []).map((p) => projectToPackageEnvironmentalCommitment(project.project_id, p)),
        commitments_hours: project.commitments_hours.map((p) => projectToPackageLabourHourCommitment(project.project_id, p)),
        commitments_spend: project.commitments_spend.map((p) => projectToPackageSocialSpendCommitment(project.project_id, p)),
        contractor_id: project.organisation_id,
        contractor_package_id: project.project_id,
        created: new Date(),
        depth: 0,
        estimated_progress: {
            accepted: 0,
            complete: 0,
            overdue: 0
        },
        financial: packageFinancial,
        financial_id: packageFinancial.financial_id,
        is_accepted: false,
        is_closed: false,
        is_draft: true,
        local_content: [],
        local_content_targets: [],
        organisation_logo: project.organisation_logo,
        organisation_name: project.organisation_name,
        package_number: packageNumber,
        package_start_date: project.project_start_date,
        package_type: packageType,
        policies: project.policies.map((p) => createDefaultPackagePolicy(project.project_id, project.project_id, p.project_policy_id)),
        project_id: project.project_id,
        representative_email: "",
        tree_path: project.project_id,
        use_labour_hour: true,
        use_local_content: true,
        use_policy: true,
        use_social_spend: true,
        use_sustainability: true
    };
};
